/**
 * Per-user storage adapter for Zustand persist.
 *
 * Two responsibilities:
 *
 *  1. Namespace the persist key by the current auth scope so two users
 *     on a shared device never see each other's local trips. The scope
 *     is either `'anonymous'` (no auth, plus the pre-login window where
 *     a visitor is planning a trip) or the user's id (the JWT `sub`).
 *
 *  2. Encrypt the persisted blob on web. AsyncStorage on web is a thin
 *     wrapper over `localStorage`, which is plain text and visible to
 *     anything running on the same origin (DevTools, extensions). On
 *     native we skip the layer — AsyncStorage already lives in the
 *     app sandbox (iOS Keychain group / Android internal storage) so
 *     the OS handles at-rest encryption.
 *
 * Threat model: defeats casual inspection of `localStorage` and forensic
 * recovery from a disk image. Does NOT defend against XSS on the app
 * origin — any code running as our JS can ask this module to decrypt.
 * That's inherent to client-side encryption.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
// Read directly from `token-storage` (not `../contexts/AuthContext`) to
// avoid an import cycle: AuthContext -> itinerary-scope ->
// scoped-encrypted-storage. `token-storage` has no app-level
// dependencies, so it's safe to pull in here.
import { getToken } from './token-storage';

const ANONYMOUS_SCOPE = 'anonymous';
const ENC_PREFIX = 'enc:v1:';
const IDB_NAME = 'kiips-secure-storage';
const IDB_STORE = 'keys';

// Feature-detect rather than branching on `Platform.OS` — same outcome
// (web has these, native doesn't) but doesn't pull `react-native` into
// the module-load graph, which lets the store load cleanly in Vitest's
// Node env without a `react-native` mock.
const isWebCryptoAvailable =
  typeof indexedDB !== 'undefined' &&
  typeof crypto !== 'undefined' &&
  typeof crypto.subtle !== 'undefined';

let _scope: string = ANONYMOUS_SCOPE;

export function getStorageScope(): string {
  return _scope;
}

/** Set the active storage scope. Pass the user's id to switch into their
 *  scope, or `null` / `undefined` for anonymous. Caller is responsible
 *  for triggering a re-hydrate of the Zustand store afterward so the
 *  newly-scoped data is read into memory. */
export function setStorageScope(scope: string | null | undefined): void {
  _scope = scope || ANONYMOUS_SCOPE;
}

function scopedKey(name: string): string {
  return `${name}:${_scope}`;
}

// ---------------------------------------------------------------------------
// Web Crypto: AES-GCM with a non-extractable CryptoKey per scope, cached in
// IndexedDB. `structuredClone` lets IDB hold the CryptoKey directly so the
// raw key material never appears in JS land.
// ---------------------------------------------------------------------------

function openIDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function loadKeyFromIDB(scope: string): Promise<CryptoKey | null> {
  try {
    const db = await openIDB();
    return await new Promise<CryptoKey | null>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const req = tx.objectStore(IDB_STORE).get(scope);
      req.onsuccess = () => resolve((req.result as CryptoKey | undefined) ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

async function saveKeyToIDB(scope: string, key: CryptoKey): Promise<void> {
  const db = await openIDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    tx.objectStore(IDB_STORE).put(key, scope);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function deleteKeyFromIDB(scope: string): Promise<void> {
  try {
    const db = await openIDB();
    await new Promise<void>((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).delete(scope);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    // Best-effort: leftover key is harmless, future writes will overwrite it.
  }
}

async function getOrCreateKey(scope: string): Promise<CryptoKey> {
  const existing = await loadKeyFromIDB(scope);
  if (existing) return existing;
  const key = await crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    false, // non-extractable
    ['encrypt', 'decrypt'],
  );
  await saveKeyToIDB(scope, key);
  return key;
}

function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function encryptString(scope: string, plaintext: string): Promise<string> {
  const key = await getOrCreateKey(scope);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = new TextEncoder().encode(plaintext);
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, data),
  );
  const combined = new Uint8Array(iv.length + ciphertext.length);
  combined.set(iv, 0);
  combined.set(ciphertext, iv.length);
  return ENC_PREFIX + bytesToBase64(combined);
}

async function decryptString(scope: string, encoded: string): Promise<string | null> {
  if (!encoded.startsWith(ENC_PREFIX)) return null;
  const key = await loadKeyFromIDB(scope);
  if (!key) return null;
  try {
    const combined = base64ToBytes(encoded.slice(ENC_PREFIX.length));
    const iv = combined.slice(0, 12);
    const ciphertext = combined.slice(12);
    const data = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
    return new TextDecoder().decode(data);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Legacy migration: a pre-update install persists under the unscoped
// `itinerary-trips` key as plain JSON. On first read we look up the user's
// stored access token to decide which scope the data belongs to (logged-in
// users get their data attributed to their userId; otherwise anonymous),
// then write it back under the scoped key and remove the legacy entry.
// One-shot per device.
// ---------------------------------------------------------------------------

function decodeJwtSub(token: string | null | undefined): string | null {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const payload = JSON.parse(
      atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/')),
    ) as Record<string, unknown>;
    return typeof payload.sub === 'string' ? payload.sub : null;
  } catch {
    return null;
  }
}

async function migrateLegacyIfPresent(name: string): Promise<string | null> {
  const legacy = await AsyncStorage.getItem(name);
  if (legacy === null) return null;
  // Access token now lives in secure storage on native (see
  // `token-storage.ts`) — read it through that module rather than
  // AsyncStorage directly, or this scope-attribution check would always
  // miss on native and misattribute logged-in users' trips to anonymous.
  const accessToken = await getToken('accessToken');
  const targetScope = decodeJwtSub(accessToken) ?? ANONYMOUS_SCOPE;
  const targetKey = `${name}:${targetScope}`;
  const toWrite = isWebCryptoAvailable
    ? await encryptString(targetScope, legacy)
    : legacy;
  await AsyncStorage.setItem(targetKey, toWrite);
  await AsyncStorage.removeItem(name);
  // Only return plaintext for callers reading the scope we migrated into.
  return targetScope === _scope ? legacy : null;
}

// ---------------------------------------------------------------------------
// Zustand storage interface. `createJSONStorage` wraps these methods and
// does the JSON parse / stringify itself, so we only deal with strings.
// ---------------------------------------------------------------------------

export const scopedEncryptedStorage = {
  async getItem(name: string): Promise<string | null> {
    const key = scopedKey(name);
    const raw = await AsyncStorage.getItem(key);
    if (raw === null) {
      // First read after the upgrade: adopt any legacy unscoped blob.
      return migrateLegacyIfPresent(name);
    }
    if (raw.startsWith(ENC_PREFIX)) {
      // Encrypted blob. If Web Crypto vanished after the write (browser
      // disabled IndexedDB, etc.) we can't recover; return null so the
      // persist layer falls back to defaults instead of handing the
      // JSON parser a chunk of base64 ciphertext.
      if (!isWebCryptoAvailable) return null;
      return decryptString(_scope, raw);
    }
    return raw;
  },
  async setItem(name: string, value: string): Promise<void> {
    const key = scopedKey(name);
    const toWrite = isWebCryptoAvailable
      ? await encryptString(_scope, value)
      : value;
    await AsyncStorage.setItem(key, toWrite);
  },
  async removeItem(name: string): Promise<void> {
    await AsyncStorage.removeItem(scopedKey(name));
  },
};

// ---------------------------------------------------------------------------
// Helpers for the login-time claim flow. These reach into the anonymous
// scope explicitly (regardless of current `_scope`) so the AuthContext can
// pull pre-login trips into the user's scope after sign-in.
// ---------------------------------------------------------------------------

/** Read and decrypt the raw persisted blob from the anonymous scope.
 *  Returns the JSON string that Zustand persist wrote, or `null` if
 *  there's nothing to claim. */
export async function readAnonymousRaw(name: string): Promise<string | null> {
  const key = `${name}:${ANONYMOUS_SCOPE}`;
  const raw = await AsyncStorage.getItem(key);
  if (raw === null) return null;
  if (raw.startsWith(ENC_PREFIX)) {
    // Encrypted but no crypto: same fallback as `getItem` — return null
    // so the claim flow treats it as "nothing to claim" rather than
    // trying to JSON.parse ciphertext.
    if (!isWebCryptoAvailable) return null;
    return decryptString(ANONYMOUS_SCOPE, raw);
  }
  return raw;
}

/** Wipe the anonymous-scope blob (plus its IDB-cached encryption key on
 *  web). Use after the claim flow has copied any trips into the user
 *  scope, or on explicit logout to keep the anonymous slot clean for the
 *  next visitor on a shared device. */
export async function clearAnonymousScope(name: string): Promise<void> {
  await AsyncStorage.removeItem(`${name}:${ANONYMOUS_SCOPE}`);
  if (isWebCryptoAvailable) {
    await deleteKeyFromIDB(ANONYMOUS_SCOPE);
  }
}

/** Exposed for tests so they can reset module state between cases. */
export function _resetForTests(): void {
  _scope = ANONYMOUS_SCOPE;
}
